import {
  type CreateMcpConnection,
  type DiscoverResult,
  DiscoverResultSchema,
  type ListMcpConnectionsQuery,
  type McpConnectionDetail,
  McpConnectionDetailSchema,
  type McpConnectionPage,
  McpConnectionPageSchema,
  type UpdateMcpConnection,
} from '@sbc/domain-models/agents';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiGet, apiSend } from './client';

const MCP_CONNECTIONS_KEY = ['admin', 'mcp-connections'];

function listKey(query: ListMcpConnectionsQuery) {
  return [...MCP_CONNECTIONS_KEY, 'list', query] as const;
}

function detailKey(id: string) {
  return [...MCP_CONNECTIONS_KEY, 'detail', id] as const;
}

export function useMcpConnections(query: ListMcpConnectionsQuery) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });
  if (query.search) {
    params.set('search', query.search);
  }
  return useQuery<McpConnectionPage>({
    queryKey: listKey(query),
    queryFn: () =>
      apiGet(`/api/admin/mcp-connections?${params.toString()}`, McpConnectionPageSchema),
  });
}

export function useMcpConnection(id: string) {
  return useQuery<McpConnectionDetail>({
    queryKey: detailKey(id),
    queryFn: () => apiGet(`/api/admin/mcp-connections/${id}`, McpConnectionDetailSchema),
  });
}

export function useCreateMcpConnection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMcpConnection) =>
      apiSend('POST', '/api/admin/mcp-connections', input, McpConnectionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MCP_CONNECTIONS_KEY }),
  });
}

export function useUpdateMcpConnection(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMcpConnection) =>
      apiSend('PUT', `/api/admin/mcp-connections/${id}`, input, McpConnectionDetailSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MCP_CONNECTIONS_KEY }),
  });
}

export function useDiscoverMcpTools(id: string) {
  return useMutation<DiscoverResult>({
    mutationFn: () =>
      apiSend('POST', `/api/admin/mcp-connections/${id}/discover`, {}, DiscoverResultSchema),
  });
}

export function useReloadMcp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiSend('POST', '/api/admin/mcp-connections/reload', {}, McpConnectionPageSchema),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MCP_CONNECTIONS_KEY }),
  });
}
